/**
 * Search form for 訪問予定表(施術師毎) — /visits/massager_init.
 *
 * Fields: date range (required) + 施術師 radio/dropdown.
 * A2/R2 label swap: はり・きゅう師 ↔ マッサージ師 (card title, radio label, error text).
 * Validation errors surface via AppModal (mirrors legacy alert() UX).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppButton, AppSelect, AppRadio, AppRadioGroup } from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import useAppModal from '@/hooks/useAppModal';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import dayjs from 'dayjs';
import type { HomeVisitMassagerMassager } from '../types';

export interface MassagerSearchFormValues {
  visitDateFrom: string;
  visitDateTo: string;
  massagerRadio: '1' | '2';
  massagerId: number | null;
}

interface Props {
  onSearch: (values: MassagerSearchFormValues) => void;
  onDateFromChange?: (isoDate: string | null) => void;
  onDateToChange?: (isoDate: string | null) => void;
  massagers: HomeVisitMassagerMassager[];
  serviceType: string;
  isLoading: boolean;
  cardTitle: string;
  /** Hydrated from URL params on first mount. null = use built-in defaults. */
  initialValues?: MassagerSearchFormValues | null;
  /** Match-count badge displayed next to the 検索 button (BD item 8). */
  totalCount?: number;
}

const HomeVisitMassagerSearchForm: React.FC<Props> = ({
  onSearch,
  onDateFromChange,
  onDateToChange,
  massagers,
  serviceType,
  isLoading,
  cardTitle,
  initialValues,
  totalCount = 0,
}) => {
  const modal = useAppModal();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const firstOfMonth = useMemo(() => convGtJDate(dayjs().startOf('month')), []);

  const [form, setForm] = useState<MassagerSearchFormValues>(
    () =>
      initialValues ?? {
        visitDateFrom: firstOfMonth,
        visitDateTo: firstOfMonth,
        massagerRadio: '1',
        massagerId: null,
      }
  );

  const updateField = useCallback(
    <K extends keyof MassagerSearchFormValues>(key: K, value: MassagerSearchFormValues[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  // BD: dropdown always pre-selects the first option (name ASC, id ASC) when:
  //  - first screen entry (massagerId=null on mount)
  //  - selected massager is no longer in the list (expired by now()/hidden/soft-delete)
  //  - after switching the radio to 指定なし (massagerId is reset)
  useEffect(() => {
    if (massagers.length === 0) return;
    if (form.massagerId != null && massagers.some(m => m.id === form.massagerId)) return;
    setForm(prev => ({ ...prev, massagerId: massagers[0].id }));
  }, [massagers, form.massagerId]);

  const massagerOptions = useMemo(
    () =>
      massagers.map(m => ({
        value: m.id,
        label: `${m.serial_number ?? ''}:${m.name}`,
      })),
    [massagers]
  );

  const handleSearch = useCallback(() => {
    if (!form.visitDateFrom || !dateValueToIso(form.visitDateFrom)) {
      modal.error({ title: '【訪問年月日開始日】を入力してください', okText: 'OK' });
      return;
    }
    if (!form.visitDateTo || !dateValueToIso(form.visitDateTo)) {
      modal.error({ title: '【訪問年月日終了日】を入力してください', okText: 'OK' });
      return;
    }
    if (form.massagerRadio === '2' && !form.massagerId) {
      modal.error({
        title: isA2 ? 'はり・きゅう師を選択してください' : 'マッサージ師を選択してください',
        okText: 'OK',
      });
      return;
    }
    onSearch(form);
  }, [form, isA2, modal, onSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <CollapsibleCard title={cardTitle}>
      <div onKeyDown={handleKeyDown}>
        <div className="grid grid-cols-[120px_1fr] items-center gap-x-4 gap-y-3">
          {/* Row 1: Date range */}
          <label className="text-sm">訪問年月日</label>
          <div className="flex items-center gap-3">
            <RA2NDatePicker
              className="flex-1"
              value={form.visitDateFrom}
              placeholder="元号00年00月00日"
              onChange={val => {
                updateField('visitDateFrom', val);
                onDateFromChange?.(dateValueToIso(val));
              }}
            />
            <span className="text-sm">～</span>
            <RA2NDatePicker
              className="flex-1"
              value={form.visitDateTo}
              placeholder="元号00年00月00日"
              onChange={val => {
                updateField('visitDateTo', val);
                onDateToChange?.(dateValueToIso(val));
              }}
            />
          </div>

          {/* Row 2: 施術師 radio + dropdown */}
          <label className="text-sm">{massagerLabel}</label>
          <div className="flex items-center gap-4">
            <AppRadioGroup
              value={form.massagerRadio}
              onChange={e => {
                const val = e.target.value as '1' | '2';
                setForm(prev => ({
                  ...prev,
                  massagerRadio: val,
                  massagerId: val === '1' ? null : (prev.massagerId ?? massagers[0]?.id ?? null),
                }));
              }}
            >
              <AppRadio value="1">指定なし</AppRadio>
              <AppRadio value="2">指定あり</AppRadio>
            </AppRadioGroup>
            <AppSelect
              className="flex-1"
              value={form.massagerId ?? undefined}
              onChange={val => updateField('massagerId', val ? Number(val) : null)}
              options={massagerOptions}
              disabled={form.massagerRadio === '1'}
            />
          </div>
        </div>

        {/* Search button (centered) + 該当件数 badge (right) — BD item 8 */}
        <div className="relative flex items-center justify-center mt-4">
          <AppButton type="primary" onClick={handleSearch} loading={isLoading}>
            <Search /> 検索
          </AppButton>
          <span className="absolute right-0 px-3 py-1 text-sm font-medium leading-tight text-center text-black -translate-y-1/2 rounded-md bg-amber-400 top-1/2">
            該当件数 {totalCount}
          </span>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default HomeVisitMassagerSearchForm;
